import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BADGE_LABELS,
  FEATURED_CATEGORY,
  FEATURED_CATEGORY_ID,
  PRODUCT_CATEGORIES,
  productsInCategory,
} from '@/content/products';
import { Badge } from './Badge';
import { Icon } from './Icon';

/**
 * 「产品」大菜单。
 *
 * 交互对齐阿里云：悬停或聚焦展开一块全宽面板，左列类目、右列该辖下的产品。
 * 关闭方式给足：移开、Esc、点击面板外、焦点离开、路由变化。
 * 触屏与窄屏不展开面板，改由移动端抽屉承担（见 Header）。
 */
export function MegaMenu({ onNavigate }: { onNavigate?: () => void }) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>(FEATURED_CATEGORY_ID);
  const wrapRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const location = useLocation();

  const cancelClose = () => {
    if (closeTimer.current !== undefined) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    // 指针在触发器与面板之间移动时会短暂离开，留一点缓冲避免闪烁。
    closeTimer.current = window.setTimeout(() => setOpen(false), 140);
  };

  useEffect(() => () => cancelClose(), []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  const active = PRODUCT_CATEGORIES.find((category) => category.id === activeId) ?? FEATURED_CATEGORY;
  const items = productsInCategory(active.id);

  return (
    <div
      ref={wrapRef}
      className="static"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onFocus={() => {
        cancelClose();
        setOpen(true);
      }}
      onBlur={(event) => {
        if (!wrapRef.current?.contains(event.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={[
          'inline-flex items-center gap-1 whitespace-nowrap rounded-btn px-3 py-2 text-sm transition-colors',
          open ? 'text-brand' : 'text-fg hover:text-brand',
        ].join(' ')}
      >
        产品
        <Icon
          name="chevronDown"
          size={14}
          className={['transition-transform', open ? 'rotate-180' : ''].join(' ')}
        />
      </button>

      {open ? (
        <div className="absolute inset-x-0 top-full border-y border-line bg-surface-1 shadow-lift">
          <div className="container-page grid grid-cols-[minmax(0,1fr)] gap-6 py-7 lg:grid-cols-[15rem_minmax(0,1fr)]">
            <div className="flex flex-col gap-1">
              {PRODUCT_CATEGORIES.map((category) => {
                const isActive = category.id === active.id;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onMouseEnter={() => setActiveId(category.id)}
                    onFocus={() => setActiveId(category.id)}
                    onClick={() => setActiveId(category.id)}
                    aria-pressed={isActive}
                    className={[
                      'flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                      isActive ? 'bg-surface-2 font-medium text-fg' : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
                    ].join(' ')}
                  >
                    <Icon name={category.icon} size={16} className={isActive ? 'text-brand' : 'text-fg-subtle'} />
                    {category.name}
                  </button>
                );
              })}
            </div>

            <div className="min-w-0">
              <p className="text-[0.8125rem] text-fg-muted">{active.summary}</p>

              <div className="mt-4 grid gap-1 sm:grid-cols-2 xl:grid-cols-3">
                {items.map((product) => (
                  <Link
                    key={product.id}
                    to={`/products#${product.id}`}
                    onClick={onNavigate}
                    className="group flex items-start gap-2.5 rounded-lg px-3 py-2.5 transition-colors hover:bg-surface-2"
                  >
                    <Icon name={product.icon} size={17} className="mt-0.5 shrink-0 text-fg-subtle group-hover:text-brand" />
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-medium text-fg">{product.name}</span>
                        {product.badge ? (
                          <Badge tone={product.badge === 'soon' ? 'tint' : 'solid'}>{BADGE_LABELS[product.badge]}</Badge>
                        ) : null}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-fg-muted">{product.tagline}</span>
                    </span>
                  </Link>
                ))}
              </div>

              <Link
                to="/products"
                onClick={onNavigate}
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-brand transition-colors hover:text-brand-hover"
              >
                查看全部产品
                <Icon name="arrowRight" size={14} />
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
